import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import MainContainer from "../../components/MainContainer";

import {
  selectCoverForTrainModePending,
  selectSelectedTrainModel,
} from "../../store/slices/model/slice";

import {
  BackButton,
  ModelTitle,
  ConfigSection,
  TutorialLink,
  SectionTitle,
  Label,
  TextInput,
  RequirementsSection,
  RequirementText,
  UploadSection,
  UploadCount,
  ActionButtons,
  DeleteButton,
  InputContainer,
  Row,
  Text,
  LabelContainer,
  FilePreviewGrid,
  FilePreview,
  FilePreviewItem,
  SkeletonLoaderColumn,
  SkeletonLoader,
  SkeletonSection,
  RowSpaceBetween,
  VideoWrapper,
  SelectButton,
  EmptyStateContainer,
  EmptyStateText,
  EmptyStateSubtext,
} from "./TrainingSelectCoverPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as StarSvg } from "../../assets/icons/star.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as TrashSvg } from "../../assets/icons/trash.svg";
import { ReactComponent as CheckSvg } from "../../assets/icons/check.svg";
import TrainingProgressComponent from "../../components/utils/TrainingProgressComponent/TrainingProgressComponent";
import Button from "../../components/utils/Button/Button";
import {
  getTrainModel,
  selectCoverForTrainModel,
} from "../../store/slices/model/asyncThunks";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { toast } from "../../components/modern-ui/sonner";

const TrainingSelectCoverPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const isLoggedIn = useSelector(selectLoggedIn);

  const trainModelId = params.modelId;

  const selectCoverLoading = useSelector(selectCoverForTrainModePending);

  const [taskName, setTaskName] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [prompt, setPrompt] = useState("");

  const [selectCover, setSelectCover] = useState(null);

  useEffect(() => {
    if (isLoggedIn && trainModelId) {
      dispatch(getTrainModel(trainModelId));
    }
  }, [trainModelId, isLoggedIn, dispatch]);

  useEffect(() => {
    if (selectedTrainModel.data) {
      const modelName = selectedTrainModel?.data?.name || "";
      const modelPrompt = selectedTrainModel?.data?.prompt || "";

      setTaskName(modelName);
      setTemplateName(modelName);
      setPrompt(modelPrompt);
    }
  }, [selectedTrainModel.data]);

  useEffect(() => {
    if (
      selectedTrainModel.data &&
      selectedTrainModel.data.collections &&
      selectedTrainModel.data.collections.length > 0 &&
      !selectCover
    ) {
      setSelectCover(selectedTrainModel.data.collections[0]._id);
    }
  }, [selectedTrainModel.data, selectCover]);

  const handleBack = () => {
    navigate("/train");
  };

  const handleTutorialClick = () => {
    navigate("/train-tutorial");
  };

  const handleSelect = (coverId) => {
    setSelectCover(coverId);
  };

  const actionSelectCover = () => {
    if (!selectCover || !templateName || templateName.length < 3) {
      toast.error(
        `Please select a cover video and enter a template name with at least 3 characters.`
      );
    }

    dispatch(
      selectCoverForTrainModel({
        trainModelId,
        collectionId: selectCover,
        templateName,
      })
    )
      .unwrap()
      .then(() => {
        navigate(`/train/completed/${trainModelId}`);
      })
      .catch(() => {
        toast.error("Error occurred while selecting cover video");
      });
  };

  // Show loading state
  if (selectedTrainModel.loading) {
    return (
      <MainContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back to Train
        </BackButton>
        <SkeletonLoaderColumn>
          <SkeletonLoader $height="26px" $width="200px" $radius="4px" />
          <SkeletonLoader $height="26px" $width="400px" $radius="4px" />
          <RowSpaceBetween>
            <SkeletonLoader $height="16px" $width="600px" $radius="4px" />
            <SkeletonLoader $height="20px" $width="40px" $radius="4px" />
          </RowSpaceBetween>
          <SkeletonLoader $height="5px" $width="100%" $radius="100px" />

          <Row>
            <SkeletonSection $height="335px">
              <SkeletonLoader $height="22px" $width="150px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
            </SkeletonSection>

            <SkeletonSection $height="335px">
              <SkeletonLoader $height="22px" $width="180px" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
            </SkeletonSection>
          </Row>

          <SkeletonSection $height="669px">
            <SkeletonLoader $height="27px" $width="140px" />

            <SkeletonLoader $height="22px" $width="160px" />
            <SkeletonLoader $height="44px" $width="500px" $radius="12px" />
            <SkeletonLoader $height="16px" $width="350px" />

            <SkeletonLoader $height="22px" $width="160px" />
            <SkeletonLoader $height="16px" $width="450px" />

            <SkeletonLoader
              style={{ marginTop: 16 }}
              $height="136px"
              $width="100%"
              $radius="16px"
            />
          </SkeletonSection>
        </SkeletonLoaderColumn>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft />
        Back to Train
      </BackButton>

      <ModelTitle>{taskName || "New Model 01"}</ModelTitle>

      <TrainingProgressComponent
        progress={75}
        title="Configure your Template"
        subtitle="Configure your template and select the cover video"
      />

      <Row>
        <ConfigSection>
          <SectionTitle>Task Configuration</SectionTitle>
          <TutorialLink onClick={handleTutorialClick}>
            📚 View our training tutorial to get started.
          </TutorialLink>

          <InputContainer>
            <Label>Task name</Label>
            <TextInput
              type="text"
              placeholder="New Model 01"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              disabled={true}
            />
          </InputContainer>

          <InputContainer>
            <Label>Prompt</Label>
            <TextInput
              type="text"
              placeholder="e.g. 'The woman turns around and walks away.'"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={true}
            />
            <RequirementText>
              A sentence that clearly and accurately describes the motion in
              your training videos.
            </RequirementText>
          </InputContainer>
        </ConfigSection>

        <RequirementsSection>
          <SectionTitle>Requirements</SectionTitle>

          <LabelContainer>
            <Label>Training Videos</Label>
            <UploadCount>10 - 20 videos</UploadCount>
          </LabelContainer>

          <Text>
            Upload 10 - 20 videos showing the motion you want to train. Each
            video should be between 3 to 5 seconds long, with a minimum
            resolution of 360p and a maximum size of 20MB.
          </Text>

          <LabelContainer>
            <Label>Testing Images</Label>
            <UploadCount>3 - 6 images</UploadCount>
          </LabelContainer>
          <Text>
            Select 3 - 6 images from the library to test the trained model.
          </Text>

          <LabelContainer>
            <Label>Cost</Label>
            <UploadCount>
              <StarSvg />
              40 Credits (40% Off)
            </UploadCount>
          </LabelContainer>
          <Text>
            Training cost is deducted when you start the training.
            <br />
            Each User has only one opportunity for a discounted trial.
          </Text>
        </RequirementsSection>
      </Row>

      <UploadSection>
        <SectionTitle>Configuration</SectionTitle>
        <InputContainer $maxWidth>
          <Label>Template Name</Label>
          <TextInput
            type="text"
            placeholder="Enter template name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
          <RequirementText>
            This will be the name of your template
          </RequirementText>
        </InputContainer>
        <InputContainer>
          <Label>Select Cover Video</Label>
          <Text>
            Choose the best video as the cover for your template (from testing
            results)
          </Text>
        </InputContainer>
        {!!selectedTrainModel.data && !!selectedTrainModel.data.collections && (
          <>
            {selectedTrainModel.data.collections.length > 0 ? (
              <FilePreviewGrid>
                {selectedTrainModel.data.collections.map((fileObj) => (
                  <FilePreviewItem
                    key={fileObj._id}
                    $selected={selectCover === fileObj._id}
                  >
                    <VideoWrapper>
                      <SelectButton
                        $selected={selectCover === fileObj._id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelect(fileObj._id);
                        }}
                        type="button"
                      >
                        {selectCover === fileObj._id && <CheckSvg />}
                      </SelectButton>

                      <FilePreview>
                        <video
                          src={fileObj.output.video.url}
                          muted
                          controls
                          preload="metadata"
                          width="100%"
                          height="auto"
                          style={{ display: "block" }}
                        />
                      </FilePreview>
                    </VideoWrapper>
                  </FilePreviewItem>
                ))}
              </FilePreviewGrid>
            ) : (
              <EmptyStateContainer>
                <EmptyStateText>No videos available</EmptyStateText>
                <EmptyStateSubtext>
                  There are no test videos generated yet for this model.
                </EmptyStateSubtext>
              </EmptyStateContainer>
            )}
          </>
        )}
      </UploadSection>

      <ActionButtons>
        <DeleteButton
          leftIcon={<TrashSvg width={15} height={15} />}
          title={"Delete"}
          style={{
            borderRadius: "100px",
            display: "flex",
            width: "130px",
            height: "44px",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#f2efe8",
            cursor: "pointer",
            fontFamily: "DM Sans, sans-serif",
            fontSize: "16px",
            fontWeight: 500,
            letterSpacing: "-0.08px",
            transition: "all 0.3s ease-in-out",
          }}
        />
        <Button
          onClick={actionSelectCover}
          loading={selectCoverLoading}
          title={"Complete Training"}
          leftIcon={<MagicStickSvg />}
        />
      </ActionButtons>
    </MainContainer>
  );
};

export default TrainingSelectCoverPage;
